// Hook para manejar la lógica de datos registrados (estado "por ubicar" o "cerrado")
// Hook for handling registered data logic (status "to locate" or "closed")
import { useMemo } from 'react';
import type { OrderResponse } from '../adapters';

interface UseRegisteredDataProps {
  shouldShowRegisteredData: boolean;
  returnedData: any;
  orders: any[];
  sortProducts: (products: any[], sortBy: string) => any[];
  sortOrders: (orders: OrderResponse[], sortBy: string) => OrderResponse[];
  productsSortBy: string;
  ordersSortBy: string;
}

export const useRegisteredData = ({
  shouldShowRegisteredData,
  returnedData,
  orders,
  sortProducts,
  sortOrders,
  productsSortBy,
  ordersSortBy,
}: UseRegisteredDataProps) => {

  // Procesar productos registrados para mostrar
  const registeredProducts = useMemo(() => {
    if (!shouldShowRegisteredData || !returnedData?.lines) return [];

    const productMap = new Map<string, any>();

    returnedData.lines.forEach((line: any) => {
      const productId = line.product_id;
      const key = `product_${productId}`;


      // Cantidad proveniente de returned-receptions (la que ya fue recepcionada/ubicada)
      // Esta será el valor mostrado a la DERECHA según requerimiento (total deseado en el UI)
      let rrQuantity = 0;
      if (returnedData.status === 0) {
        // Estado "Cerrado" - usar la cantidad ubicada (sumatoria de sub-líneas)
        rrQuantity = (line.lines || []).reduce(
          (acc: number, subLine: any) => acc + (subLine.quantity || 0),
          0
        );
      } else {
        // Estados "Abierto" (1) y "Por ubicar" (2) - usar quantity de la línea
        rrQuantity = Number(line.quantity || 0);
      }

      // Buscar cantidad total en las órdenes (incluyendo packs)
      // y priorizar datos de producto provenientes de la línea si existen
      let totalQuantity = 0;
      let productData: any = line.product || null;

      if (orders && orders.length > 0) {
        for (const order of orders) {
          // Buscar en líneas directas del pedido
          const foundLine = order.lines?.find(
            (l: any) =>
              l.product_id === productId || l.product_id === Number(productId)
          );
          if (foundLine) {
            totalQuantity += Number(foundLine.qty || 0);
            if (!productData && foundLine.product) {
              productData = foundLine.product;
            }
          }

          // Buscar dentro de líneas de pack
          order.lines?.forEach((orderLine: any) => {
            const packLines = orderLine.pack?.lines || [];
            packLines.forEach((packLine: any) => {
              // Asegurar comparación de tipos correcta
              if (
                packLine.product_id === productId ||
                packLine.product_id === Number(productId)
              ) {
                totalQuantity += Number(packLine.qty || 0);
                if (!productData && packLine.product) {
                  productData = packLine.product;
                }
              }
            });
          });
        }
      }

      // Fallback en caso de que no se encuentre la cantidad en órdenes
      // Evita mostrar 0/0 y mantiene la UI informativa aunque falte información
      if (totalQuantity === 0 && typeof line.quantity === 'number') {
        totalQuantity = Number(line.quantity) || 0;
      }

      // Según actualización: izquierda = returned-receptions.quantity, derecha = orders.qty
      const leftFromReturned = Number(rrQuantity || 0);
      const rightFromOrders = Number(totalQuantity || 0);

      if (productMap.has(key)) {
        const existing = productMap.get(key);
        existing.processedQuantity += leftFromReturned; // ahora izquierda = returned
        existing.totalQuantity += rightFromOrders;      // derecha = orders
      } else {
        productMap.set(key, {
          id: String(productId),
          name: productData?.name || `Producto ${productId}`,
          code: productData?.code || `BP${String(productId).padStart(6, '0')}`,
          image: productData?.image_hash || '/imagen/example.webp',
          processedQuantity: leftFromReturned,
          totalQuantity: rightFromOrders,
        });
      }
    });

    return Array.from(productMap.values());
  }, [shouldShowRegisteredData, returnedData, orders]);

  // Procesar órdenes registradas para mostrar
  const registeredOrders = useMemo(() => {
    if (!shouldShowRegisteredData || !orders) return [];

    return orders.map((order: any) => ({
      id: order.id,
      ref: order.ref,
      external_id: order.external_id || '',
      shop_id: order.shop_id || 0,
      status_id: order.status_id || 0,
      country_id: order.country_id || 0,
      status: order.status || { id: 0, code: 0, description: '' },
      payment_method_id: order.payment_method_id || 0,
      total: order.total || 0,
      total_tax: order.total_tax || 0,
      total_shipping: order.total_shipping || 0,
      total_discount: order.total_discount || 0,
      currency: order.currency || 'EUR',
      customer: order.customer || {},
      shipment: order.shipment || {},
      external_payment_method: order.external_payment_method || '',
      external_financial_status: order.external_financial_status || '',
      fulfilled: order.fulfilled || null,
      stock_location_order_id: order.stock_location_order_id || 0,
      preparation_id: order.preparation_id || 0,
      order_created_at: order.order_created_at,
      order_paid_at: order.order_paid_at || null,
      errors: order.errors || [],
      canceled_at: order.canceled_at || null,
      confirmed_at: order.confirmed_at || null,
      ready_to_prepare_at: order.ready_to_prepare_at || null,
      shop: order.shop,
      lines: order.lines || [],
    }));
  }, [shouldShowRegisteredData, orders]);

  // Productos registrados ordenados
  const sortedRegisteredProducts = useMemo(() => {
    return sortProducts(registeredProducts, productsSortBy);
  }, [registeredProducts, productsSortBy, sortProducts]);

  // Órdenes registradas ordenadas
  const sortedRegisteredOrders = useMemo(() => {
    return sortOrders(registeredOrders, ordersSortBy);
  }, [registeredOrders, ordersSortBy, sortOrders]);

  return {
    registeredProducts,
    registeredOrders,
    sortedRegisteredProducts,
    sortedRegisteredOrders,
  };
};


